import React, { memo, useImperativeHandle, forwardRef } from 'react'
import PropTypes from 'prop-types'

import { TextField as TextFieldBase, TextFieldBorderless as TextFieldBorderlessBase, TextareaAutosize as TextareaAutosizeBase } from '../Controls'
import { handleRequired, ErrorMessage, useFormBase, formImperativeHandleFunctions, handleFormPostObject } from './common'
import { FormFieldContainer } from './constants'
import { useTranslation } from 'react-i18next'

/**
 * Implements Mui TextField for forms.
 *
 * @param {object} props component props
 * @returns {TextField} TextField component
 */
const TextField = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fixedErrorMessageFieldHeight, fieldTemplate, data, label, startString, ...finalProps } = props

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled),
    validate: (postObject, refs) => {
      let value = valueRef.current || ''
      if (typeof value === 'string') {
        value = value.trim()
      }
      let error = ''

      if (fieldTemplate.isRequired && !value) {
        error = handleRequired(props)
      } else if (fieldTemplate.maxLength && value.length > fieldTemplate.maxLength) {
        error = `${t('forms.maxFieldLength')} ${fieldTemplate.maxLength}.`
      } else if (fieldTemplate.maxValue !== undefined && parseFloat(value) > fieldTemplate.maxValue) {
        error = `${t('forms.maxValue')} ${fieldTemplate.maxValue}.`
      } else if (fieldTemplate.minValue !== undefined && parseFloat(value) < fieldTemplate.minValue) {
        error = `${t('forms.minValue')} ${fieldTemplate.minValue}.`
      } else if (fieldTemplate.regx && fieldTemplate.regxError && !fieldTemplate.regx.test(value)) {
        error = t(fieldTemplate.regxError)
      } else if (fieldTemplate.validator) {
        const ret = fieldTemplate.validator(value)
        if (ret) {
          error = t(ret)
        } else {
          error = ''
        }
      } 

      return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
    },
  }))
  return (
    <FormFieldContainer>
      <TextFieldBase
        {...finalProps}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        placeholder={fieldTemplate?.placeholder}
        ref={refFormElement}
        onChange={onChange}
        error={!!errorMsg}
        noTranslate={true}
        label={t(`${fieldLabel.current}`)}
        startString={startString || fieldTemplate?.startString}
      />
      <ErrorMessage errorMsg={(errorMsg)} errorMsgFixedHeight={errorMsgFixedHeight} />
    </FormFieldContainer>
  )
}))

TextField.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the value is changed.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the startString.
   */
  noTranslateStartString: PropTypes.bool,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', 'outlined']),
  /**
   Adds a fixed height at the bottom of the control for error messages.
   */
  fixedErrorMessageFieldHeight: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to focus the cursor to this control.
   */
  'ref.current.focus': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

TextField.defaultProps = {
  noTranslate: false
}

export default TextField

export const TextFieldOutlined = props =>
  <TextField
    {...props}
    variant='outlined'
  />

TextFieldOutlined.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
     Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
     onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
     Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
     Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
     Attributes applied to the InputLabel element.
   */
  InputLabelProps: PropTypes.object,
  /**
     The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
     Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
     Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
     MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
     Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
     Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
     Prevents the language translation of the startString.
   */
  noTranslateStartString: PropTypes.bool,
  /**
     MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', 'outlined']),
  /**
     Adds a fixed height at the bottom of the control for error messages.
   */
  fixedErrorMessageFieldHeight: PropTypes.bool,
  /**
     Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
     The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
     Set a ref to the component in your page and call this function to focus the cursor to this control.
   */
  'ref.current.focus': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
     Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
}

TextFieldOutlined.defaultProps = {
  noTranslate: false,
  variant: 'outlined'
}

/**
 * Implements TextFieldBorderless for forms.
 *
 * @param {object} props component props
 * @returns {TextFieldBorderless} TextField component
 */
export const TextFieldBorderless = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fixedErrorMessageFieldHeight, fieldTemplate, data, startString, syncedDisplayError, ...finalProps } = props

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  const shownError = errorMsg || syncedDisplayError || ''
  const reserveErrorSpace = fixedErrorMessageFieldHeight && !!shownError

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled),
    validate: (postObject, refs) => {
      let value = valueRef.current
      if (typeof value === 'string') {
        value = value.trim()
      }
      let error = ''

      if (fieldTemplate.isRequired && !value) {
        error = handleRequired(props)
      } else if (fieldTemplate.maxLength && value.length > fieldTemplate.maxLength) {
        error = `${t('forms.maxFieldLength')} ${fieldTemplate.maxLength}.`
      } else if (fieldTemplate.maxValue !== undefined && parseFloat(value) > fieldTemplate.maxValue) {
        error = `${t('forms.maxValue')} ${fieldTemplate.maxValue}.`
      } else if (fieldTemplate.minValue !== undefined && parseFloat(value) < fieldTemplate.minValue) {
        error = `${t('forms.minValue')} ${fieldTemplate.minValue}.`
      } else if (fieldTemplate.regx && fieldTemplate.regxError && !fieldTemplate.regx.test(value)) {
        error = t(fieldTemplate.regxError)
      } else if (fieldTemplate.validator) {
        const ret = fieldTemplate.validator(value)
        if (ret) {
          error = t(ret)
        }
      } 

      return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
    },
  }))
  return (
    <FormFieldContainer>
      <TextFieldBorderlessBase
        {...finalProps}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        placeholder={fieldTemplate.placeholder}
        ref={refFormElement}
        onChange={onChange}
        error={!!shownError}
        noTranslate={true}
        label={t(`${fieldLabel.current}`)}
        startString={startString || fieldTemplate.startString}
      />
      <ErrorMessage
        errorMsg={shownError}
        errorMsgFixedHeight={reserveErrorSpace ? errorMsgFixedHeight : '0px'}
      />
    </FormFieldContainer>
  )
}))

TextFieldBorderless.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Parent-driven validation message (e.g. table row validation); shown with any local errorMsg.
   */
  syncedDisplayError: PropTypes.string,
  /**
   onChange function called when the value is changed.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the startString.
   */
  noTranslateStartString: PropTypes.bool,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', 'outlined']),
  /**
   Adds a fixed height at the bottom of the control for error messages.
   */
  fixedErrorMessageFieldHeight: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to focus the cursor to this control.
   */
  'ref.current.focus': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

/**
 * Implements TextareaAutosize for forms.
 *
 * @param {object} props component props
 * @returns {TextareaAutosize} TextField component
 */
export const TextareaAutosize = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fieldTemplate, data, startString, ...finalProps } = props
  const { onChange, disabled, setDisabled, fieldLabel, refFormElement, valueRef } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setDisabled),
  }))
  return (
    <FormFieldContainer>
      <TextareaAutosizeBase
        {...finalProps}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        placeholder={fieldTemplate.placeholder}
        ref={refFormElement}
        onChange={onChange}
        noTranslate={true}
        label={t(`${fieldLabel.current}`)}
        startString={startString || fieldTemplate.startString}
      />
    </FormFieldContainer>
  )
}))

TextareaAutosize.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   onChange function called when the value is changed.
   */
  onChange: PropTypes.func,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.string,
  /**
   Attributes applied to the input element.
   */
  InputProps: PropTypes.object,
  /**
   The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Fixed text that appears to the left of the input text.
   */
  startString: PropTypes.string,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the startString.
   */
  noTranslateStartString: PropTypes.bool,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['standard', 'outlined']),
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to focus the cursor to this control.
   */
  'ref.current.focus': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

TextField.defaultProps = {
  noTranslate: false
}